import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Grid } from "@mui/material";
import { Icon } from "@iconify/react";
import PageTitle from "../../components/PageTitle";
import CardCount from "../../components/CardCount";
import Table from "../../components/Table/Table";
import Button from "../../components/Button";
import Layout from "../../components/Layout";
import { useNavigate } from "react-router-dom";
import Modal from "../../components/Modal";
import InputGroup from "../../components/FormComponent/InputGroup";
import SelectGroup from "../../components/FormComponent/SelectGroup";
import styles from "../../components/FormComponent/FormStyles.module.css";
import NoData from "../dashboard/components/NotFoundCard/NoData";
import NoAccount from "../../assets/images/emptyacct.png";

import Badge from "../../components/Badge";
import useStyles from "./styles";
import { httprequest } from "../../data/api";

const WALLET_SUMMARY_ENDPOINT = "/api/v1/points/wallet-summary";
const BANK_DETAILS_ENDPOINT = "/api/v1/bank-details/all";

const Wallet = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [modalStep, setModalStep] = useState(0);
  const [hasBank, setHasBank] = useState(false);
  const [walletData, setWalletData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [redeemDetails, setRedeemDetails] = useState({
    amount: "",
    bank: ""
  });
  const [selectedBankIndex, setSelectedBankIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const getWalletSummary = async () => {
    try {
      const res = await httprequest.get(WALLET_SUMMARY_ENDPOINT);
      const walletData = res?.data?.data;
      setWalletData(walletData);

      if (walletData?.earning_history) {
        const allActivities = [];
        walletData.earning_history.forEach((history) => {
          history.activities.forEach((activity) => {
            allActivities.push({
              ...activity,
              date: new Date(activity.timestamp).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric"
              }),
              activity: activity.tag_name,
              status: "approved"
            });
          });
        });
        setActivities(allActivities);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const getBankDetails = async () => {
    try {
      const res = await httprequest.get(BANK_DETAILS_ENDPOINT);
      const banks = res?.data?.data?.bank_details || [];
      setBankAccounts(banks);
      setHasBank(banks.length > 0);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  useEffect(() => {
    getWalletSummary();
    getBankDetails();
  }, []);

  const columns = [
    { name: "Activity", selector: (row) => row.activity, sortable: true, style: { textTransform: "capitalize" } },
    { name: "Points", selector: (row) => `+${row.points}`, sortable: true },
    { name: "Date", selector: (row) => row.date, sortable: true },
    { name: "Status", selector: (row) => row.status, sortable: true, cell: (row) => <Badge status={row?.status} /> }
  ];

  const handleRedeemContinue = () => {
    if (!redeemDetails.bank || redeemDetails.bank === "Select Bank") {
      toast.error("Please select a bank");

      return;
    }
    if (!redeemDetails.amount) {
      toast.error("Please enter an amount");

      return;
    }
    if (Number(redeemDetails.amount) <= 0) {
      toast.error("Please enter a valid amount");

      return;
    }
    if (Number(redeemDetails.amount) > Number(walletData?.available_points ?? 0)) {
      toast.error("Insufficient points");

      return;
    }

    if (hasBank) {
      setModalStep(2);
    } else {
      setModalStep(1);
    }
  };

  const handleCloseModal = () => {
    setShowRedeemModal(false);
    setModalStep(0);
    setRedeemDetails({ amount: "", bank: "" });
  };

  const handleConfirmWithdrawal = async () => {
    if (!bankAccounts || bankAccounts.length === 0) {
      toast.error("No bank account available for withdrawal.");

      return;
    }

    try {
      setIsLoading(true);
      const selectedBank = bankAccounts[selectedBankIndex];
      const payload = {
        withdrawal_amount: Number(redeemDetails.amount),
        bank_details: {
          account_name: selectedBank.account_name,
          account_number: selectedBank.account_number,
          bank_name: selectedBank.bank_name
        }
      };

      const response = await httprequest.post("/api/v1/points/redeem", payload);
      toast.success(response?.data?.message || "Redemption successful");
      handleCloseModal();
      getWalletSummary(); // Refresh wallet data after successful withdrawal
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to redeem points");
    } finally {
      setIsLoading(false);
    }
  };

  const renderRedeemForm = () => (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <SelectGroup label="Bank">
            <select
              className={styles.select}
              value={redeemDetails.bank}
              onChange={(e) => setRedeemDetails({ ...redeemDetails, bank: e.target.value })}
            >
              <option>Select Bank</option>
              {bankAccounts.map((bank, index) => (
                <option key={index} value={bank.bank_name}>{bank.bank_name}</option>
              ))}
            </select>
          </SelectGroup>
        </Grid>
        <Grid item xs={12} md={6}>
          <InputGroup
            label="Available coin"
            value={walletData?.available_points ?? "0"}
            disabled={true}
            placeHolder="0"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputGroup
            label="You Receive"
            placeHolder=""
            value={redeemDetails.amount}
            onChange={(e) => setRedeemDetails({ ...redeemDetails, amount: e.target.value })}
            inputType="number"
          />
        </Grid>
        <Grid item xs={12}>
          <div className={classes.marginTop20}>
            <Button
              variant="primary"
              buttonName="Continue"
              buttonSize="full"
              color="btndefault"
              onClick={handleRedeemContinue}
            />
          </div>
        </Grid>
      </Grid>
    </div>
  );

  const renderNoBank = () => (
    <div className={classes.noBankContainer}>
      <NoData
        img={NoAccount}
        title="No Bank Added"
        subtitle="There has been no bank account added for payments yet."
      />
      <div
        className={classes.addAccountLink}
        onClick={() => navigate("/wallet/my-bank")}
      >
        <Icon icon="ph:plus-bold" /> Add an Account
      </div>
    </div>
  );

  const renderWithdrawal = () => (
    <div>
      <div className={classes.withdrawalContainer}>
        {bankAccounts.map((bank, index) => (
          <div key={index} className={selectedBankIndex === index ? classes.bankOptionSelected : classes.bankOption}>
            <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
              <input
                type="radio"
                name="bank"
                checked={selectedBankIndex === index}
                onChange={() => setSelectedBankIndex(index)}
                className={classes.radioInput}
              />
              <div className={classes.bankDetails}>
                <div className="bankName">{bank.bank_name}</div>
                <div className="accountNumber">*******{bank.account_number.slice(-4)}</div>
              </div>
            </div>
            {index === 0 && <Icon icon="ph:pencil-simple" className={classes.editIcon} onClick={() => navigate("/wallet/my-bank")} />}
          </div>
        ))}
      </div>

      <div className={classes.actionButtons}>
        <Button
          variant="primaryoutline"
          buttonName="Add an Account"
          buttonSize="full"
          color="btnfontprimary"
          onClick={() => navigate("/wallet/my-bank")}
        />
        <Button
          variant="primary"
          buttonName={isLoading ? "Processing..." : "Confirm withdrawal"}
          buttonSize="full"
          color="btndefault"
          disabled={isLoading}
          onClick={handleConfirmWithdrawal}
        />
      </div>
    </div>
  );

  const getModalContent = () => {
    switch (modalStep) {
    case 1: return renderNoBank();
    case 2: return renderWithdrawal();
    default: return renderRedeemForm();
    }
  };

  const getModalTitle = () => {
    if (modalStep === 2) return "Withdrawal";

    return "Redeem Point";
  };

  const getModalSubtitle = () => {
    if (modalStep === 2) return "Choose how to receive your point";
    if (modalStep === 1) return "Convert your points into rewards";

    return "Convert your points into approved rewards.";
  };

  return (
    <Layout
      children={
        <div>
          <PageTitle
            title="Wallet"
            subtitle="Manage your points and transactions"
            button={
              <div className={classes.flexGap10}>
                <Button
                  variant="primaryoutline"
                  buttonName="My Bank"
                  buttonSize="fluid"
                  color="btnfontprimary"
                  onClick={() => navigate("/wallet/my-bank")}
                />
                <Button
                  buttonName="Redeem Point"
                  variant="primary"
                  buttonSize="fluid"
                  color="btndefault"
                  buttonIcon={<Icon icon="ph:coins" style={{ marginRight: 5 }} />}
                  onClick={() => setShowRedeemModal(true)}
                />
              </div>
            }
          />

          <Grid container spacing={3} className={classes.marginBottom2rem}>
            <Grid item xs={12} sm={6} md={3}>
              <CardCount
                title="Total Earned"
                count={walletData?.total_earned ?? 0}
                icon={<Icon icon="ph:wallet-bold" width={30} />}
                leftSubText="Last Month"
                leftSubValue="+0"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardCount
                title="Available Points"
                count={walletData?.available_points ?? 0}
                icon={<Icon icon="ph:trend-up-bold" width={30} />}
                leftSubText="This Year"
                leftSubValue="0"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardCount
                title="Maximum Withdrawal"
                count={walletData?.maximum_withdrawal ?? 0}
                icon={<Icon icon="ph:trend-down-bold" width={30} />}
                leftSubText="Lifetime"
                leftSubValue="0"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardCount
                title="Pending Points"
                count={walletData?.pending_points ?? 0}
                icon={<Icon icon="ph:clock-bold" width={30} />}
                leftSubText="Processing"
                leftSubValue="0"
              />
            </Grid>
          </Grid>

          <div className={classes.transactionHistory}>
            <Table
              data={activities}
              columns={columns}
              pagination
            />
          </div>

          <Modal
            isopen={showRedeemModal}
            closeModal={handleCloseModal}
            title={getModalTitle()}
            subtitle={getModalSubtitle()}
            modalContent={getModalContent()}
          />
        </div>
      }
    />
  );
};

export default Wallet;
